import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { TranslationPrefixes } from "consts/translationPrefixes";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { getSingleData } from "services/service";
import { RoutesUrl } from "../../common/consts/Routes";
import { CredentialsData } from "../CredentialsForm/CredentialsForm";
import CardComponent from "../shared/CardComponent";
import {
  AMAZON_USA_CATEGORIES,
  SELLER_TYPES,
} from "./constants";
import {
  ProductDataFilters as FiltersType,
  ProductDataRow,
  SellerType,
} from "./types";
import {
  fetchProductData,
  saveProductDataExtract,
} from "../../services/productDataService";
import ProductDataResults from "./ProductDataResults";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formRow: {
      marginBottom: theme.spacing(2),
    },
    sectionTitle: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1),
    },
    fetchButton: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  })
);

const DEFAULT_FILTERS: FiltersType = {
  marketplace: "USA",
  categories: [],
  keywords: "",
  priceMin: undefined,
  priceMax: undefined,
  salesMin: undefined,
  salesMax: undefined,
  reviewsMin: undefined,
  reviewsMax: undefined,
  sellerTypes: [...SELLER_TYPES],
  categoryRankMin: undefined,
  categoryRankMax: undefined,
};

export default function ProductDataService() {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const prefix = TranslationPrefixes.productDataService;

  const [filters, setFilters] = useState<FiltersType>(DEFAULT_FILTERS);
  const [products, setProducts] = useState<ProductDataRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState<CredentialsData | null>(null);

  useEffect(() => {
    getSingleData("amazonCredentials").then((data) => {
      if (data) {
        const raw = data as unknown as Record<string, string | undefined>;
        setCredentials({
          api_key_name: raw.api_key_name ?? "",
          api_key: raw.api_key ?? ""
        });
      }
    });
  }, []);

  const currencySymbol = filters.marketplace === "USA" ? "$" : "";

  const updateFilter = useCallback(<K extends keyof FiltersType>(
    key: K,
    value: FiltersType[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleCategoryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value;
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    updateFilter("categories", arr as string[]);
  };

  const handleSellerTypeChange = (seller: SellerType, checked: boolean) => {
    setFilters((prev) => {
      const next = checked
        ? [...prev.sellerTypes, seller]
        : prev.sellerTypes.filter((s) => s !== seller);
      return { ...prev, sellerTypes: next };
    });
  };

  const handleFetchData = async () => {
    const keyName = credentials?.api_key_name?.trim();
    const key = credentials?.api_key?.trim();
    if (!keyName || !key) {
      toast.warn(t(`${prefix}credentialsRequired`));
      history.push(RoutesUrl.credentialsForm);
      return;
    }
    setLoading(true);
    setProducts([]);
    try {
      const result = await fetchProductData(filters, keyName, key);
      setProducts(result);
      toast.success(t(`${prefix}fetchSuccess`));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      const is403 = msg.includes("invalid") || msg.includes("Forbidden") || msg.includes("403");
      toast.error(is403 ? t(`${prefix}invalidCredentials`) : (msg || t(`${prefix}fetchError`)));
      if (is403) {
        history.push(RoutesUrl.credentialsForm);
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToDb = async () => {
    if (products.length === 0) return;
    try {
      await saveProductDataExtract(filters, products);
      toast.success(t(`${prefix}savedToDb`));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t(`${prefix}saveError`);
      toast.error(msg);
      console.error(e);
    }
  };

  return (
    <CardComponent title={t(`${prefix}headline`)} isLoading={loading}>
      <Box>
      <Grid container spacing={2}>
        {/* Marketplace */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t(`${prefix}marketplace`)}</InputLabel>
            <Select
              value={filters.marketplace}
              onChange={(e) => updateFilter("marketplace", e.target.value as "USA")}
              label={t(`${prefix}marketplace`)}
            >
              <MenuItem value="USA">USA</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Categories */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t(`${prefix}categories`)}</InputLabel>
            <Select
              multiple
              value={filters.categories}
              onChange={handleCategoryChange}
              label={t(`${prefix}categories`)}
              renderValue={(selected) => (selected as string[]).join(", ") || t(`${prefix}chooseCategories`)}
            >
              {AMAZON_USA_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Products */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={t(`${prefix}keywordsInListings`)}
            placeholder={t(`${prefix}keywordsPlaceholder`)}
            value={filters.keywords}
            onChange={(e) => updateFilter("keywords", e.target.value)}
          />
        </Grid>

        {/* Price range */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={`${t(`${prefix}priceMin`)} (${currencySymbol})`}
            value={filters.priceMin ?? ""}
            onChange={(e) => updateFilter("priceMin", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={`${t(`${prefix}priceMax`)} (${currencySymbol})`}
            value={filters.priceMax ?? ""}
            onChange={(e) => updateFilter("priceMax", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        {/* Sales range */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={t(`${prefix}salesMin`)}
            value={filters.salesMin ?? ""}
            onChange={(e) => updateFilter("salesMin", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={t(`${prefix}salesMax`)}
            value={filters.salesMax ?? ""}
            onChange={(e) => updateFilter("salesMax", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 0 }}
          />
        </Grid>

        {/* Reviews range */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={t(`${prefix}reviewsMin`)}
            value={filters.reviewsMin ?? ""}
            onChange={(e) => updateFilter("reviewsMin", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={t(`${prefix}reviewsMax`)}
            value={filters.reviewsMax ?? ""}
            onChange={(e) => updateFilter("reviewsMax", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 0 }}
          />
        </Grid>

        {/* Seller types */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {t(`${prefix}sellerTypes`)}
          </Typography>
          <FormGroup row>
            {SELLER_TYPES.map((seller) => (
              <FormControlLabel
                key={seller}
                control={
                  <Checkbox
                    checked={filters.sellerTypes.includes(seller)}
                    onChange={(_, checked) => handleSellerTypeChange(seller, checked)}
                    name={seller}
                  />
                }
                label={seller}
              />
            ))}
          </FormGroup>
        </Grid>

        {/* Category rank range */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={t(`${prefix}categoryRankMin`)}
            value={filters.categoryRankMin ?? ""}
            onChange={(e) => updateFilter("categoryRankMin", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            label={t(`${prefix}categoryRankMax`)}
            value={filters.categoryRankMax ?? ""}
            onChange={(e) => updateFilter("categoryRankMax", e.target.value === "" ? undefined : Number(e.target.value))}
            inputProps={{ min: 1 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            className={classes.fetchButton}
            variant="contained"
            color="primary"
            onClick={handleFetchData}
            disabled={loading}
          >
            {t(`${prefix}fetchData`)}
          </Button>
        </Grid>
      </Grid>

      {products.length > 0 && (
        <Box className={classes.sectionTitle}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveToDb}
            style={{ marginBottom: 16 }}
          >
            {t(`${prefix}saveToDb`)}
          </Button>
          <ProductDataResults products={products} currencySymbol={currencySymbol} />
        </Box>
      )}
      </Box>
    </CardComponent>
  );
}
